import React from 'react';
import { Field, Form, FormikProps } from 'formik';
import { Box, Button, Card, Flex, Heading, Tooltip, Icon, Spinner } from 'pounceV2';
import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import BaseDetectionFormEditorSectionV3 from 'Components/forms/BaseDetectionForm/BaseDetectionFormEditorSection/BaseDetectionFormEditorSectionV3';
import { ExtractedFilterCondition } from 'Components/forms/DetectionFieldFiltersForm/constants';
import SaveButtonV3 from 'Components/buttons/SaveButton/SaveButtonV3';
import { formatFiltersAsDSF } from 'Pages/Build/Detections/utils';
import { RuleDetails } from 'Source/graphql/fragments/RuleDetails.generated';
import { FormikSwitchV3 } from 'Components/fields/Switch';
import BaseSimpleDetectionFormEditorSectionV3 from 'Components/forms/BaseDetectionForm/BaseDetectionFormEditorSection/BaseSimpleDetectionFormEditorSectionV3';
import FormikCheckboxV3 from 'Components/fields/Checkbox/CheckboxV3';
import RuleDetailsBannerV3 from 'Components/sidesheets/RuleFilterSidesheet/RuleDetailsBanner/RuleDetailsBannerV3';
import useIsCreatePROnSaveEnabled from 'Hooks/useIsCreatePROnSaveEnabled';
import {
  RuleFilterSidesheetNewFilterEntry,
  RuleFilterSidesheetValues,
} from './RuleFilterSidesheetV3';
import RuleFilterSidesheetFormAlertV3 from './RuleFilterSidesheetFormAlertV3';

export type RuleFilterSidesheetFormProps = {
  newFilter?: RuleFilterSidesheetNewFilterEntry | RuleFilterSidesheetNewFilterEntry[];
  error?: string;
  isSuccess?: boolean;
  onDiscard?: () => void;
  onClose?: (showModal: boolean) => void;
  additionalSuccessMessageData?: Record<string, any>;
} & FormikProps<RuleFilterSidesheetValues>;
const RuleFilterSidesheetFormV3: React.FC<RuleFilterSidesheetFormProps> = ({
  newFilter,
  error,
  isSuccess = false,
  onDiscard,
  isValid,
  values,
  setValues,
  resetForm,
  onClose,
  additionalSuccessMessageData,
}) => {
  const { isEnabled: isCreatePROnSaveEnabled, isLoading: isLoadingCreatePROnSaveEnabled } =
    useIsCreatePROnSaveEnabled();
  const { alertEventValues, addTest, ...ruleDetails } = values;

  React.useEffect(() => {
    if (newFilter) {
      setValues(currentValues => {
        // `newFilter` uses RuleFilterSidesheetNewFilterEntry (`operator` optional), but inline
        // filter state needs ExtractedFilterCondition (`operator` required). We seed `operator`
        // with `!=` and spread `f` afterward so a caller-supplied `operator` overrides. The map
        // callback's return type lets TypeScript verify each row instead of casting the array.
        const additions = (Array.isArray(newFilter) ? newFilter : [newFilter]).map(
          (f): ExtractedFilterCondition => ({
            operator: '!=',
            ...f,
          }),
        );
        const updatedInlineFiltersGUI = {
          ...currentValues.inlineFiltersGUI,
          values: currentValues.inlineFiltersGUI?.values?.concat(additions) || additions,
        };

        return {
          ...currentValues,
          inlineFiltersGUI: updatedInlineFiltersGUI,
          // Starting with the GUI data format because it is easiest to add the new filter on to
          // (since it is in the correct format already), plus users cannot even edit raw YAML for
          // filters at this time.
          inlineFilters: formatFiltersAsDSF(updatedInlineFiltersGUI),
        };
      });
    }
  }, [newFilter, setValues]);

  const handleAddTest = React.useCallback(
    ({ target: { checked } }) => {
      const tests = [...ruleDetails.tests];

      if (checked) {
        tests.push({
          expectedResult: false,
          name: `Alert Test - ${new Date().toISOString()}`,
          resource: JSON.stringify(alertEventValues || {}),
          mocks: [],
        });
      } else {
        tests.pop();
      }
      setValues({
        ...ruleDetails,
        addTest: checked,
        tests,
      });
    },
    [setValues, ruleDetails, alertEventValues],
  );

  const handleDiscardChanges = React.useCallback(() => {
    resetForm();
    onClose(false);
    if (onDiscard) {
      onDiscard();
    }
  }, [onClose, onDiscard, resetForm]);

  const getSaveButtonText = React.useCallback(() => {
    if (isCreatePROnSaveEnabled) {
      return 'Create PR';
    }
    return <Box as="span">Save &amp; Run Test</Box>;
  }, [isCreatePROnSaveEnabled]);

  return (
    <Flex as={Form} direction="column" justify="space-between" height="100%">
      <Flex direction="column">
        <Heading variant="h5" fontWeight="bold">
          Add Filter
        </Heading>
        <Heading variant="h5" mt={9} mb={5}>
          Target:
        </Heading>
        <RuleDetailsBannerV3
          rule={ruleDetails as RuleDetails}
          omitBadges
          omitControls
          linkToRule={true}
          linkExternal={true}
        />
        <Card position="relative" mt={5} variant="transparent" borderWidth={0}>
          <ErrorBoundaryV3>
            {ruleDetails.isSimpleDetection ? (
              <BaseSimpleDetectionFormEditorSectionV3
                type={ruleDetails.analysisType}
                isUpdate
                isManaged={ruleDetails.managed}
              />
            ) : (
              <BaseDetectionFormEditorSectionV3
                type={ruleDetails.analysisType}
                isUpdate
                isManaged={ruleDetails.managed}
                functionOpen={false}
              />
            )}
          </ErrorBoundaryV3>
        </Card>
        <Flex mt={8} direction="column" gap={2}>
          <Heading variant="h5">
            <Flex align="center" gap={2}>
              Unit Test:
              {ruleDetails.managed && (
                <Tooltip content={<Box>Unit Test is created and managed by the Panther team.</Box>}>
                  <Icon type="info" size="medium" />
                </Tooltip>
              )}
            </Flex>
          </Heading>
          <Field
            as={FormikCheckboxV3}
            name="addTest"
            onChange={handleAddTest}
            label="Add the current alert event as a unit test"
            disabled={ruleDetails.managed}
          />
          {addTest && (
            <Flex>
              <Field
                as={FormikSwitchV3}
                name={`tests[${(ruleDetails.tests || []).length - 1}].expectedResult`}
                label="The detection should trigger based on the example event:"
                disabled={ruleDetails.managed}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <Box mt={5}>
        {(isSuccess || error || !isValid) && (
          <RuleFilterSidesheetFormAlertV3
            ruleDetails={ruleDetails}
            error={error}
            isSuccess={isSuccess}
            addTest={addTest}
            isValid={isValid}
            additionalSuccessMessageData={additionalSuccessMessageData}
          />
        )}
        <Flex gap={4} my={20}>
          <Button variant="outline" aria-label="Close drawer" onClick={() => onClose(true)}>
            Close
          </Button>
          <Button
            variant="outline"
            aria-label="Discard Filter Changes"
            onClick={handleDiscardChanges}
          >
            Discard Changes
          </Button>
          <SaveButtonV3
            allowInvalidSubmission
            data-tid="add_rule_filter_from_alert_details"
            data-testid="add_rule_filter_from_alert_details"
            {...(isLoadingCreatePROnSaveEnabled && { disabled: true })}
          >
            {isLoadingCreatePROnSaveEnabled ? <Spinner size="small" /> : getSaveButtonText()}
          </SaveButtonV3>
        </Flex>
      </Box>
    </Flex>
  );
};

export default RuleFilterSidesheetFormV3;
